import * as Tabs from '@studio/design-system/tabs';
import type { ReactElement, ReactNode } from 'react';
import { twMerge } from 'cn';

export interface TabItem {
  value: string;
  label: string;
  content: ReactNode;
  /**
   * Optional STag props for this tab's trigger, e.g. `STagNew(Sel.X, 'tab',
   * value)`. Opt-in per call site: TabsPanel is shared, and only the panels a
   * test drives need an addressable trigger. Per rule:
   * playwright-stag-selectors-only (ai.knowledge/platform) — a Playwright spec
   * must not reach a tab by role or label text.
   */
  triggerStag?: { 'data-stag': string };
}

type ControlledProps = {
  value: string;
  onChange: (value: string) => void;
  defaultValue?: never;
};

type UncontrolledProps = {
  defaultValue: string;
  value?: never;
  onChange?: never;
};

export type TabsPanelProps = {
  className?: string;
  tabs: TabItem[];
  contentClassName?: string;
  listClassName?: string;
  triggerClassName?: string;
  applySubcategoryTriggerClassName?: boolean;
  thickerBorder?: boolean;
} & (ControlledProps | UncontrolledProps);

/**
 * A wrapper around the Tabs.Root component that provides a more convenient API for
 * creating tabs with content.
 */
export const TabsPanel = ({
  className,
  tabs,
  defaultValue,
  value,
  onChange,
  contentClassName,
  listClassName,
  triggerClassName,
  applySubcategoryTriggerClassName = false,
  thickerBorder = true // defaulting to true, since, that is what the new design system uses
}: TabsPanelProps): ReactElement => {
  const rootProps = value !== undefined ? { value, onChange } : { defaultValue };

  return (
    <Tabs.Root className={twMerge('grow', className)} {...rootProps}>
      <Tabs.List
        className={twMerge(
          'mb-28 gap-28 text-md',
          applySubcategoryTriggerClassName ? 'border-b-0' : '',
          listClassName
        )}
      >
        {tabs.map((tab) => (
          <Tabs.Trigger
            key={tab.value}
            value={tab.value}
            {...tab.triggerStag}
            className={twMerge(
              triggerClassName,
              thickerBorder
                ? 'data-[state="active"]:border-b-[2px] data-[state="active"]:border-primary'
                : ''
            )}
          >
            {tab.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {tabs.map((tab) => (
        <Tabs.Content
          key={tab.value}
          value={tab.value}
          className={twMerge('flex grow flex-col data-[state="inactive"]:hidden', contentClassName)}
        >
          {tab.content}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );
};
